import { DUPE_API_URL, WEB_AUTH_TOKEN_PARAM } from '@dupe/common/constants'
import { signInWithCustomToken, signOut } from 'firebase/auth'
import { useRouter } from 'next/compat/router'
import { useRouter as useAppRouter } from 'next/navigation'
import { useCallback, useEffect, useRef, useState } from 'react'
import useAuthUser from '~/src/hooks/useAuthUser'
import auth from '~/src/lib/firebase/firebaseAuth'
import logger from '~/src/lib/logger'

interface VerifyAuthWebTokenResponse {
  customToken: string
  uid: string
}

export const useAuthWebToken = () => {
  // next/compat/router: the pages NextRouter when mounted under pages/, null
  // under app/ (where query params are read from window.location below).
  const compatRouter = useRouter()
  // next/navigation's router works under both routers (the pages router is
  // adapted onto it), so the redirect push needs no branching.
  const appRouter = useAppRouter()
  const { authUser, loading: loadingAuthUser } = useAuthUser()
  const [isProcessing, setIsProcessing] = useState(false)
  const hasProcessedRef = useRef(false)
  const [wasAuthWebTokenActive, setWasAuthWebTokenActive] = useState(false)

  const handleRedirect = useCallback(
    (path: string) => {
      appRouter.push(path)
    },
    [appRouter]
  )

  // Verify the auth web token with the API
  const verifyAuthWebToken = useCallback(
    async (
      authWebToken: string
    ): Promise<VerifyAuthWebTokenResponse | null> => {
      try {
        const response = await fetch(
          `${DUPE_API_URL}/api/web-app-auth/verify`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ authWebToken }),
          }
        )

        if (!response.ok) {
          throw new Error('Invalid or expired auth web token')
        }

        return await response.json()
      } catch (error) {
        logger.error('Error verifying auth web token:', { error })
        return null
      }
    },
    []
  )

  const cleanupUrlParams = useCallback(() => {
    // Clean up the session-auth parameter from the URL
    if (compatRouter) {
      // Pages router: shallow replace strips the params without re-running
      // data fetching.
      const { pathname, query } = compatRouter
      const cleanQuery = { ...query }
      delete cleanQuery['session-auth']
      delete cleanQuery.uid
      delete cleanQuery.redirect

      compatRouter.replace(
        {
          pathname,
          query: cleanQuery,
        },
        undefined,
        { shallow: true }
      )
    } else {
      // App Router has no shallow router.replace — strip the params via
      // history.replaceState (the documented App Router shallow-routing
      // equivalent).
      const url = new URL(window.location.href)
      url.searchParams.delete('session-auth')
      url.searchParams.delete('uid')
      url.searchParams.delete('redirect')
      window.history.replaceState(null, '', url.toString())
    }
  }, [compatRouter])

  const signInWithCustomTokenAndCleanup = useCallback(
    async (customToken: string, uid: string) => {
      try {
        // Sign in with Firebase using custom token
        const userCredential = await signInWithCustomToken(auth, customToken)

        if (!userCredential) {
          throw new Error('Failed to sign in with custom token')
        }

        logger.debug('Successfully authenticated via auth web token', { uid })

        // Clean up URL parameters on success
        cleanupUrlParams()

        return userCredential
      } catch (error) {
        logger.error('Error signing in with custom token:', { error })
        throw error
      }
    },
    [cleanupUrlParams]
  )

  // Process auth web token authentication when the route is ready and auth
  // state is loaded. Query params are read from window.location inside the
  // effect (client-only, router-agnostic): useSearchParams() here would force
  // a CSR bailout for every page since this hook renders inside AppProviders.
  // Under pages the values match router.query once isReady; the session-auth
  // handoff always arrives as a document load, so a mount-time read suffices
  // under app/.
  useEffect(() => {
    // Pages router: wait for the query string to be hydrated. The app router
    // has no isReady equivalent — it is always ready.
    if (compatRouter && !compatRouter.isReady) return
    if (isProcessing || hasProcessedRef.current) return
    if (loadingAuthUser) return

    const searchParams = new URLSearchParams(window.location.search)
    const sessionAuthParam = searchParams.get(WEB_AUTH_TOKEN_PARAM)
    const uidParam = searchParams.get('uid')
    const redirectPath = searchParams.get('redirect')

    // Only process if we have both session-auth and uid parameters
    if (!sessionAuthParam || !uidParam) return

    // Check if current user matches the UID from parameters
    if (authUser) {
      if (authUser.uid === uidParam) {
        // Same user, no need to process - remove URL params
        cleanupUrlParams()
        return
      } else {
        // Different user, log out current user first
        logger.debug(
          'Logging out current user to switch to different user via auth web token auth',
          {
            currentUid: authUser.uid,
            targetUid: uidParam,
          }
        )
        signOut(auth)
        return // Let the effect run again after sign out
      }
    }

    setIsProcessing(true)
    hasProcessedRef.current = true
    setWasAuthWebTokenActive(true)

    const processAuth = async () => {
      try {
        // Verify the auth web token
        const result = await verifyAuthWebToken(sessionAuthParam)

        if (!result) {
          logger.warn('Failed to verify auth web token')
          cleanupUrlParams() // Clean up URL parameters on failure
          return
        }

        // Verify the UID matches
        if (result.uid !== uidParam) {
          logger.warn('UID mismatch in auth web token authentication', {
            providedUid: uidParam,
            tokenUid: result.uid,
          })
          cleanupUrlParams() // Clean up URL parameters on failure
          return
        }

        // Sign in with the custom token
        await signInWithCustomTokenAndCleanup(result.customToken, result.uid)

        // if redirect param provided take the user to that page
        if (redirectPath) {
          handleRedirect(redirectPath)
        }
      } catch (error) {
        logger.error('Error processing auth web token authentication:', {
          error,
        })
        cleanupUrlParams() // Clean up URL parameters on failure
      } finally {
        setIsProcessing(false)
      }
    }

    processAuth()
  }, [
    compatRouter,
    authUser,
    loadingAuthUser,
    isProcessing,
    verifyAuthWebToken,
    signInWithCustomTokenAndCleanup,
    cleanupUrlParams,
    handleRedirect,
  ])

  return {
    isProcessing,
    hasProcessed: hasProcessedRef.current,
    isAuthWebTokenActive: isProcessing || hasProcessedRef.current,
    wasAuthWebTokenActive,
  }
}
